import React, { useState, useMemo } from "react";

// Locked conversion constants — derived from real 90-day content performance data.
// EDIT COMMISSION_RATE to match the real average commission % before launch.
const REACH_TO_CONVO_RATE = 0.00122;
const RESPONSE_RATE = 0.10;
const CONVO_TO_CONSULT_RATE = 0.10;
const COMMISSION_RATE = 0.03;

const ACCENT = "#FE4F4E";
const TRACK_BG = "#E5E5E5";

const formatCurrency = (n) => "$" + Math.round(n).toLocaleString("en-US");
const formatNumber = (n) => Math.round(n).toLocaleString("en-US");

const Slider = ({ testId, label, value, min, max, step, onChange, format, hint }) => {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <div className="mb-7 md:mb-8 last:mb-0">
      <div className="flex items-end justify-between gap-4 mb-3">
        <label className="font-dm-sans text-sm md:text-[0.95rem] font-medium text-neutral-700">
          {label}
        </label>
        <span data-testid={`${testId}-value`} className="font-dm-serif text-2xl md:text-[1.75rem] text-neutral-950 tabular-nums leading-none">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="ch-slider w-full"
        style={{
          background: `linear-gradient(to right, ${ACCENT} 0%, ${ACCENT} ${percent}%, ${TRACK_BG} ${percent}%, ${TRACK_BG} 100%)`,
        }}
        aria-label={label}
      />
      {hint && (
        <p className="mt-3 font-dm-sans text-xs text-neutral-500 italic leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
};

const StatTile = ({ testId, value, label }) => (
  <div
    data-testid={testId}
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 md:p-6"
  >
    <div className="font-dm-serif text-3xl md:text-[2.25rem] text-white tabular-nums leading-none">
      {value}
    </div>
    <div className="mt-3 font-dm-sans font-semibold text-[0.6rem] md:text-[0.65rem] tracking-[0.18em] uppercase text-neutral-400 leading-snug">
      {label}
    </div>
  </div>
);

const RevenueCalculator = () => {
  const [salePrice, setSalePrice] = useState(700000);
  const [accountsReached, setAccountsReached] = useState(115041);
  const [closeRate, setCloseRate] = useState(30);

  const calc = useMemo(() => {
    const closeRateDecimal = closeRate / 100;
    const openingConversations = accountsReached * REACH_TO_CONVO_RATE;
    const responses = openingConversations * RESPONSE_RATE;
    const consultations = responses * CONVO_TO_CONSULT_RATE;
    const signedClients = consultations * closeRateDecimal;
    const revenuePerMonth = signedClients * salePrice * COMMISSION_RATE;
    const revenuePerYear = revenuePerMonth * 12;
    return {
      openingConversations,
      consultations,
      signedClients,
      revenuePerYear,
    };
  }, [salePrice, accountsReached, closeRate]);

  return (
    <section data-testid="calculator-section" className="py-20 md:py-28 border-t border-neutral-200 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="font-helvetica text-[0.7rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500 mb-6">
            The Math
          </p>
          <h2 data-testid="calculator-title" className="font-akira uppercase tracking-tight text-neutral-900 text-3xl md:text-5xl leading-[0.95]">
            What's this actually<br />
            <span className="text-neutral-400">worth to you?</span>
          </h2>
          <p className="mt-6 font-advercase text-base md:text-lg text-neutral-500 max-w-xl mx-auto leading-relaxed">
            Drag the sliders. See your projected commission based on real 90-day content performance data.
          </p>
        </div>

        {/* Two-panel grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 items-stretch">
          {/* LEFT — Inputs */}
          <div
            data-testid="calculator-inputs"
            className="rounded-[1.25rem] p-7 md:p-10 shadow-[0_1px_0_0_rgba(0,0,0,0.03),0_30px_70px_-40px_rgba(0,0,0,0.18)]"
            style={{ background: "#F4F4F4" }}
          >
            <div className="font-dm-sans font-semibold text-[0.65rem] tracking-[0.32em] uppercase text-neutral-500 mb-7 md:mb-9">
              Your Inputs
            </div>
            <Slider
              testId="slider-sale-price"
              label="Average Home Sale Price"
              value={salePrice}
              min={200000}
              max={3000000}
              step={25000}
              onChange={setSalePrice}
              format={formatCurrency}
            />
            <Slider
              testId="slider-accounts-reached"
              label="Monthly Accounts Reached"
              value={accountsReached}
              min={10000}
              max={400000}
              step={5000}
              onChange={setAccountsReached}
              format={formatNumber}
            />
            <Slider
              testId="slider-close-rate"
              label="Consultation Close Rate"
              value={closeRate}
              min={25}
              max={35}
              step={1}
              onChange={setCloseRate}
              format={(v) => `${v}%`}
              hint="Most agents close 25–35% of consultations into signed agreements."
            />
          </div>

          {/* RIGHT — Output */}
          <div
            data-testid="calculator-output"
            className="relative overflow-hidden rounded-[1.25rem] p-7 md:p-10 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.5)]"
            style={{ background: "#1a1a1a" }}
          >
            {/* Soft accent corner glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-32 -right-32 w-72 h-72 rounded-full blur-3xl"
              style={{ background: ACCENT, opacity: 0.1 }}
            />
            <div className="relative">
              <div className="font-dm-sans font-semibold text-[0.65rem] md:text-[0.7rem] tracking-[0.32em] uppercase text-neutral-400 mb-4">
                Projected Commission / Year
              </div>
              <div
                data-testid="projected-commission"
                className="font-dm-serif text-5xl md:text-7xl leading-[0.95] tabular-nums"
                style={{ color: ACCENT }}
              >
                {formatCurrency(calc.revenuePerYear)}
              </div>

              <div className="mt-9 md:mt-11 grid grid-cols-2 gap-3 md:gap-4">
                <StatTile
                  testId="tile-opening-conversations"
                  value={formatNumber(calc.openingConversations)}
                  label={<>Opening Conversations <br className="hidden md:inline" />/ Mo</>}
                />
                <StatTile
                  testId="tile-consultations"
                  value={formatNumber(calc.consultations)}
                  label={<>Consultations Booked <br className="hidden md:inline" />/ Mo</>}
                />
                <StatTile
                  testId="tile-signed-clients"
                  value={(calc.signedClients * 12).toFixed(1)}
                  label={<>Signed Clients <br className="hidden md:inline" />/ Year</>}
                />
                <StatTile
                  testId="tile-revenue-year"
                  value={formatCurrency(calc.revenuePerYear)}
                  label={<>Revenue <br className="hidden md:inline" />/ Year</>}
                />
              </div>

              <p className="mt-7 md:mt-8 font-dm-sans text-xs md:text-[0.78rem] italic text-neutral-500 leading-relaxed">
                Based on real 90-day content performance data. Projections are illustrative, not a guarantee of results.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevenueCalculator;
